import React, { useRef, useEffect } from "react";
import countriesLib from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

// Register locale once at module load
countriesLib.registerLocale(enLocale as any);

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  baseFieldStyle: (field: string, hasValue: boolean) => React.CSSProperties;
  fieldTextStyle: React.CSSProperties;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  focused: string | null;
  setFocused: (field: string | null) => void;
  setAndValidate: (field: string, value: string) => void;
  setTouched: (touched: Record<string, boolean>) => void;
}

type Country = { code: string; name: string };

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  baseFieldStyle,
  fieldTextStyle,
  errors,
  touched,
  focused,
  setFocused,
  setAndValidate,
  setTouched,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const countries: Country[] = React.useMemo(() => {
    const names = countriesLib.getNames("en", { select: "official" }) as Record<string, string>;
    return Object.entries(names)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const selectedCountry = countries.find((c) => c.code === value);

  const hasError = Boolean(errors.country && touched.country);
  const hasValue = Boolean(value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen]);

  const handleSelect = (code: string) => {
    onChange(code);
    setIsOpen(false);
    if (touched.country) setAndValidate("country", code);
    setTouched({ ...touched, country: true });
    setAndValidate("country", code);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocused("country");
    } else {
      setFocused(focused === "country" ? null : focused);
    }
  };

  const handleBlur = () => {
    // Delay to allow click events to fire
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setTouched({ ...touched, country: true });
        setAndValidate("country", value);
        setFocused(focused === "country" ? null : focused);
      }
    }, 200);
  };

  return (
    <div className="md:col-span-2 relative">
      <label htmlFor="country" className="sr-only">Country</label>
      <button
        ref={buttonRef}
        type="button"
        id="country"
        name="country"
        onClick={handleToggle}
        onBlur={handleBlur}
        onFocus={() => setFocused("country")}
        className={`w-full appearance-none rounded border bg-transparent px-4 py-3 pr-10 text-left text-white outline-none focus:border-white/60 h-[46px] md:h-auto ${
          !hasValue ? "text-white/60" : ""
        }`}
        style={{
          ...baseFieldStyle("country", hasValue),
          fontSize: '16px'
        }}
        aria-describedby={hasError ? "country-error" : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {selectedCountry ? selectedCountry.name : "Select Country*"}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/70">
        <svg
          width="14"
          height="8"
          viewBox="0 0 14 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M1 1L7 7L13 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[#0b0f17] border border-white/20 rounded shadow-lg max-h-[300px] overflow-y-auto p-1"
          role="listbox"
        >
          <button
            type="button"
            onClick={() => handleSelect("")}
            className={`w-full text-left px-4 py-2.5 text-white hover:bg-[#a83e32] focus:bg-[#a83e32] focus:outline-none flex items-center gap-2 rounded ${
              !value ? "bg-[#a83e32]" : ""
            }`}
            style={{
              ...fieldTextStyle,
              fontSize: '16px'
            }}
            role="option"
            aria-selected={!value}
          >
            {!value && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path
                  d="M11.6667 3.5L5.25 9.91667L2.33333 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span>Select Country*</span>
          </button>
          {countries.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => handleSelect(c.code)}
              className={`w-full text-left px-4 py-2.5 text-white hover:bg-[#a83e32] focus:bg-[#a83e32] focus:outline-none flex items-center gap-2 rounded ${
                value === c.code ? "bg-[#a83e32]" : ""
              }`}
              style={{
                ...fieldTextStyle,
                fontSize: '16px'
              }}
              role="option"
              aria-selected={value === c.code}
            >
              {value === c.code && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M11.6667 3.5L5.25 9.91667L2.33333 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <span>{c.name}</span>
            </button>
          ))}
        </div>
      )}

      {hasError && (
        <p
          id="country-error"
          role="alert"
          className="text-white mt-1"
          style={{
            ...fieldTextStyle,
            fontSize: 14,
            lineHeight: "140%",
            textShadow:
              "0px 0px 11.96px rgba(255,0,0,0.5), 0px 0px 23.92px #FF0000",
          }}
        >
          * {errors.country}
        </p>
      )}
    </div>
  );
};

export default CountrySelect;
